'use client';

import { useState, useCallback } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import ScoreGauge from '@/components/tools/ScoreGauge';
import ProgressBar from '@/components/tools/ProgressBar';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface FormData {
  // Motivation
  currentSatisfaction: number; // 1-5
  clarityOfTarget: string;
  reasonForChange: string;
  // Readiness
  timelineFlexibility: string;
  educationNeeds: string;
  willingnessToStartOver: string;
  // Financial
  financialRunwayMonths: number;
  willingToTakePayCut: string;
  secondIncomeSource: string;
  // Skills
  transferableSkillsCount: string;
  networkInTargetField: string;
  relevantExperience: string;
}

interface DimensionScore {
  label: string;
  score: number;
}

interface TransitionPhase {
  name: string;
  duration: string;
  icon: string;
  color: string;
  tasks: string[];
}

interface Results {
  overall: number;
  dimensions: DimensionScore[];
  readinessLabel: string;
  readinessDescription: string;
  transitionPlan: TransitionPhase[];
  topInsights: string[];
}

/* ------------------------------------------------------------------ */
/*  Questions data                                                     */
/* ------------------------------------------------------------------ */

interface QuestionDef {
  id: keyof FormData;
  question: string;
  subtitle: string;
  accent: string;
  icon: string;
  type: 'slider' | 'select';
  sliderMin?: number;
  sliderMax?: number;
  sliderUnit?: string;
  options?: { value: string; label: string }[];
}

const QUESTIONS: QuestionDef[] = [
  {
    id: 'currentSatisfaction',
    question: 'How satisfied are you with your current career?',
    subtitle: 'Current State',
    accent: 'border-blue-400 bg-blue-50',
    icon: 'M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    type: 'slider',
    sliderMin: 1,
    sliderMax: 5,
    sliderUnit: '',
  },
  {
    id: 'clarityOfTarget',
    question: 'How clear are you about the career you want to move into?',
    subtitle: 'Target Clarity',
    accent: 'border-purple-400 bg-purple-50',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    type: 'select',
    options: [
      { value: 'very-clear', label: 'Very clear -- I know exactly what I want to do' },
      { value: 'somewhat-clear', label: 'Somewhat clear -- I have a general direction' },
      { value: 'exploring', label: 'Still exploring -- I have a few ideas' },
      { value: 'no-idea', label: 'No idea yet -- I just know I want something different' },
    ],
  },
  {
    id: 'reasonForChange',
    question: 'What is the primary driver behind your desire to change careers?',
    subtitle: 'Motivation',
    accent: 'border-emerald-400 bg-emerald-50',
    icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
    type: 'select',
    options: [
      { value: 'passion', label: 'Passion -- I want to pursue something I love' },
      { value: 'growth', label: 'Growth -- I have hit a ceiling in my current field' },
      { value: 'values', label: 'Values -- my current work no longer aligns with who I am' },
      { value: 'burnout', label: 'Burnout -- I need to get out for my well-being' },
      { value: 'compensation', label: 'Compensation -- I want better pay or benefits' },
      { value: 'external', label: 'External -- my industry is declining or unstable' },
    ],
  },
  {
    id: 'timelineFlexibility',
    question: 'How flexible is your timeline for making this transition?',
    subtitle: 'Timeline',
    accent: 'border-orange-400 bg-orange-50',
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    type: 'select',
    options: [
      { value: 'no-rush', label: 'No rush -- I can take 1-2 years if needed' },
      { value: 'moderate', label: 'Moderate -- I want to transition within 6-12 months' },
      { value: 'soon', label: 'Soon -- I want to move within 3-6 months' },
      { value: 'urgent', label: 'Urgent -- I need to change as soon as possible' },
    ],
  },
  {
    id: 'educationNeeds',
    question: 'How much additional education or training do you need for your target career?',
    subtitle: 'Education Gap',
    accent: 'border-indigo-400 bg-indigo-50',
    icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
    type: 'select',
    options: [
      { value: 'none', label: 'None -- I already have the required qualifications' },
      { value: 'minor', label: 'Minor -- a short course or certification would suffice' },
      { value: 'moderate', label: 'Moderate -- I need several months of training' },
      { value: 'significant', label: 'Significant -- I may need a degree or extensive program' },
    ],
  },
  {
    id: 'willingnessToStartOver',
    question: 'How willing are you to start at a lower seniority level in your new field?',
    subtitle: 'Readiness Mindset',
    accent: 'border-teal-400 bg-teal-50',
    icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
    type: 'select',
    options: [
      { value: 'fully', label: 'Fully willing -- I am ready to start fresh' },
      { value: 'somewhat', label: 'Somewhat willing -- for the right opportunity' },
      { value: 'reluctant', label: 'Reluctant -- I would prefer to maintain my level' },
      { value: 'not-willing', label: 'Not willing -- I expect a lateral or upward move' },
    ],
  },
  {
    id: 'financialRunwayMonths',
    question: 'How many months of living expenses do you have saved?',
    subtitle: 'Financial Safety Net',
    accent: 'border-amber-400 bg-amber-50',
    icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    type: 'slider',
    sliderMin: 0,
    sliderMax: 12,
    sliderUnit: 'mo',
  },
  {
    id: 'willingToTakePayCut',
    question: 'Are you prepared to take a pay cut during your transition?',
    subtitle: 'Financial Flexibility',
    accent: 'border-red-400 bg-red-50',
    icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
    type: 'select',
    options: [
      { value: 'yes-significant', label: 'Yes -- up to 30% or more reduction is acceptable' },
      { value: 'yes-moderate', label: 'Yes -- up to 15-20% reduction' },
      { value: 'yes-small', label: 'Slightly -- up to 10% reduction' },
      { value: 'no', label: 'No -- I need to match or exceed my current salary' },
    ],
  },
  {
    id: 'secondIncomeSource',
    question: 'Do you have a secondary income source or financial backup?',
    subtitle: 'Financial Backup',
    accent: 'border-cyan-400 bg-cyan-50',
    icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z',
    type: 'select',
    options: [
      { value: 'yes-strong', label: 'Yes -- spouse/partner income or substantial investments' },
      { value: 'yes-some', label: 'Yes -- freelance, side gig, or small passive income' },
      { value: 'no', label: 'No -- my primary job is my only income' },
    ],
  },
  {
    id: 'transferableSkillsCount',
    question: 'How many of your current skills transfer directly to your target career?',
    subtitle: 'Transferable Skills',
    accent: 'border-violet-400 bg-violet-50',
    icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    type: 'select',
    options: [
      { value: '8+', label: '8 or more -- most of my skills are highly transferable' },
      { value: '5-7', label: '5 to 7 -- a solid foundation to build on' },
      { value: '3-4', label: '3 to 4 -- some relevant skills' },
      { value: '1-2', label: '1 to 2 -- very few overlap' },
      { value: 'none', label: 'None -- I am starting from scratch' },
    ],
  },
  {
    id: 'networkInTargetField',
    question: 'How strong is your professional network in the target field?',
    subtitle: 'Network Strength',
    accent: 'border-rose-400 bg-rose-50',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    type: 'select',
    options: [
      { value: 'strong', label: 'Strong -- I have mentors, contacts, and connections there' },
      { value: 'moderate', label: 'Moderate -- I know a few people in the industry' },
      { value: 'weak', label: 'Weak -- I have one or two loose connections' },
      { value: 'none', label: 'None -- I do not know anyone in my target field' },
    ],
  },
  {
    id: 'relevantExperience',
    question: 'Do you have any direct experience in your target field (volunteering, side projects, freelance)?',
    subtitle: 'Relevant Experience',
    accent: 'border-lime-400 bg-lime-50',
    icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    type: 'select',
    options: [
      { value: 'significant', label: 'Yes -- I have meaningful hands-on experience' },
      { value: 'some', label: 'Some -- a small project or brief freelance work' },
      { value: 'learning', label: 'Only learning -- courses, reading, or observation' },
      { value: 'none', label: 'None -- I have not tried it yet' },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Default form values                                                */
/* ------------------------------------------------------------------ */

const DEFAULT_FORM: FormData = {
  currentSatisfaction: 2,
  clarityOfTarget: 'somewhat-clear',
  reasonForChange: 'growth',
  timelineFlexibility: 'moderate',
  educationNeeds: 'minor',
  willingnessToStartOver: 'somewhat',
  financialRunwayMonths: 3,
  willingToTakePayCut: 'yes-moderate',
  secondIncomeSource: 'no',
  transferableSkillsCount: '3-4',
  networkInTargetField: 'weak',
  relevantExperience: 'learning',
};

/* ------------------------------------------------------------------ */
/*  Satisfaction slider labels                                         */
/* ------------------------------------------------------------------ */

const SATISFACTION_LABELS: Record<number, string> = {
  1: 'Very Dissatisfied',
  2: 'Dissatisfied',
  3: 'Neutral',
  4: 'Satisfied',
  5: 'Very Satisfied',
};

/* ------------------------------------------------------------------ */
/*  Scoring functions                                                  */
/* ------------------------------------------------------------------ */

function scoreMotivation(form: FormData): number {
  let score = 0;

  // Current satisfaction (lower satisfaction = higher motivation to change)
  // 1 = very dissatisfied -> 30pts, 5 = very satisfied -> 5pts
  const satisfactionPts = [0, 30, 25, 15, 8, 5];
  score += satisfactionPts[form.currentSatisfaction] ?? 15;

  // Clarity of target (0-35 points)
  if (form.clarityOfTarget === 'very-clear') score += 35;
  else if (form.clarityOfTarget === 'somewhat-clear') score += 25;
  else if (form.clarityOfTarget === 'exploring') score += 14;
  else score += 5;

  // Reason for change (0-35 points)
  const reasonScores: Record<string, number> = {
    passion: 35,
    values: 32,
    growth: 28,
    burnout: 22,
    compensation: 18,
    external: 15,
  };
  score += reasonScores[form.reasonForChange] ?? 15;

  return Math.min(100, score);
}

function scoreReadiness(form: FormData): number {
  let score = 0;

  // Timeline flexibility (0-30 points) -- more time = more prepared
  if (form.timelineFlexibility === 'no-rush') score += 30;
  else if (form.timelineFlexibility === 'moderate') score += 24;
  else if (form.timelineFlexibility === 'soon') score += 14;
  else score += 6;

  // Education needs (0-35 points) -- less needed = more ready
  if (form.educationNeeds === 'none') score += 35;
  else if (form.educationNeeds === 'minor') score += 26;
  else if (form.educationNeeds === 'moderate') score += 14;
  else score += 5;

  // Willingness to start over (0-35 points)
  if (form.willingnessToStartOver === 'fully') score += 35;
  else if (form.willingnessToStartOver === 'somewhat') score += 24;
  else if (form.willingnessToStartOver === 'reluctant') score += 12;
  else score += 4;

  return Math.min(100, score);
}

function scoreFinancialSafety(form: FormData): number {
  let score = 0;

  // Financial runway months (0-40 points)
  if (form.financialRunwayMonths >= 12) score += 40;
  else if (form.financialRunwayMonths >= 9) score += 35;
  else if (form.financialRunwayMonths >= 6) score += 28;
  else if (form.financialRunwayMonths >= 3) score += 18;
  else if (form.financialRunwayMonths >= 1) score += 8;
  else score += 0;

  // Willingness to take pay cut (0-30 points)
  if (form.willingToTakePayCut === 'yes-significant') score += 30;
  else if (form.willingToTakePayCut === 'yes-moderate') score += 22;
  else if (form.willingToTakePayCut === 'yes-small') score += 14;
  else score += 4;

  // Second income source (0-30 points)
  if (form.secondIncomeSource === 'yes-strong') score += 30;
  else if (form.secondIncomeSource === 'yes-some') score += 18;
  else score += 4;

  return Math.min(100, score);
}

function scoreSkillsTransferability(form: FormData): number {
  let score = 0;

  // Transferable skills count (0-35 points)
  if (form.transferableSkillsCount === '8+') score += 35;
  else if (form.transferableSkillsCount === '5-7') score += 28;
  else if (form.transferableSkillsCount === '3-4') score += 18;
  else if (form.transferableSkillsCount === '1-2') score += 8;
  else score += 2;

  // Network in target field (0-30 points)
  if (form.networkInTargetField === 'strong') score += 30;
  else if (form.networkInTargetField === 'moderate') score += 20;
  else if (form.networkInTargetField === 'weak') score += 10;
  else score += 2;

  // Relevant experience (0-35 points)
  if (form.relevantExperience === 'significant') score += 35;
  else if (form.relevantExperience === 'some') score += 24;
  else if (form.relevantExperience === 'learning') score += 12;
  else score += 3;

  return Math.min(100, score);
}

function buildTransitionPlan(form: FormData, motivation: number, readiness: number, financial: number, skills: number): TransitionPhase[] {
  const phases: TransitionPhase[] = [];

  // Phase 1: Research
  const researchTasks: string[] = [];
  if (form.clarityOfTarget === 'no-idea' || form.clarityOfTarget === 'exploring') {
    researchTasks.push('Take career assessments and identify 3-5 target roles that match your strengths');
    researchTasks.push('Conduct informational interviews with 5-10 people in fields that interest you');
  } else {
    researchTasks.push('Deep-dive into your target role: required skills, salary ranges, and growth outlook');
  }
  researchTasks.push('Research day-to-day realities of your target career through blogs, podcasts, and shadowing');
  if (form.networkInTargetField === 'none' || form.networkInTargetField === 'weak') {
    researchTasks.push('Join 2-3 professional communities or associations in your target field');
  }
  researchTasks.push('Map your transferable skills to your target role requirements and identify gaps');

  phases.push({
    name: 'Research',
    duration: form.clarityOfTarget === 'very-clear' ? '2-4 weeks' : '4-8 weeks',
    icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    color: 'from-blue-500 to-cyan-500',
    tasks: researchTasks,
  });

  // Phase 2: Prepare
  const prepareTasks: string[] = [];
  if (form.educationNeeds === 'significant') {
    prepareTasks.push('Enroll in a degree program, bootcamp, or intensive training relevant to your target career');
  } else if (form.educationNeeds === 'moderate') {
    prepareTasks.push('Complete 2-3 focused online courses or certifications to close your skill gaps');
  } else if (form.educationNeeds === 'minor') {
    prepareTasks.push('Earn a relevant certification or complete a targeted short course');
  }
  if (form.financialRunwayMonths < 6) {
    prepareTasks.push(`Build your emergency fund to at least 6 months of expenses before making the leap`);
  }
  if (form.relevantExperience === 'none' || form.relevantExperience === 'learning') {
    prepareTasks.push('Start a side project, volunteer role, or freelance gig in your target field to build real experience');
  }
  prepareTasks.push('Update your resume to highlight transferable skills and reframe past experience for your new direction');
  prepareTasks.push('Rebuild your LinkedIn profile with your target career as the focus');
  if (form.networkInTargetField !== 'strong') {
    prepareTasks.push('Proactively network: attend industry events, reach out on LinkedIn, and find a mentor in your target field');
  }

  phases.push({
    name: 'Prepare',
    duration: form.educationNeeds === 'significant' ? '3-12 months' : form.educationNeeds === 'moderate' ? '2-4 months' : '1-3 months',
    icon: 'M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z',
    color: 'from-purple-500 to-pink-500',
    tasks: prepareTasks,
  });

  // Phase 3: Transition
  const transitionTasks: string[] = [];
  if (form.willingToTakePayCut === 'no') {
    transitionTasks.push('Target roles that match your current compensation -- focus on companies that value cross-functional experience');
  } else {
    transitionTasks.push('Apply strategically to bridge roles or entry points in your target field that leverage your existing strengths');
  }
  transitionTasks.push('Craft a compelling career change narrative for your cover letters and interviews');
  transitionTasks.push('Practice answering "Why are you changing careers?" with confidence and specificity');
  if (form.willingnessToStartOver === 'fully' || form.willingnessToStartOver === 'somewhat') {
    transitionTasks.push('Consider contract, freelance, or part-time roles as stepping stones into your new field');
  }
  transitionTasks.push('Set weekly job search targets: applications, networking conversations, and follow-ups');

  phases.push({
    name: 'Transition',
    duration: form.timelineFlexibility === 'urgent' ? '1-2 months' : '2-4 months',
    icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
    color: 'from-amber-500 to-orange-500',
    tasks: transitionTasks,
  });

  // Phase 4: Launch
  const launchTasks: string[] = [];
  launchTasks.push('Negotiate your offer thoughtfully -- consider total compensation, growth potential, and learning opportunities');
  launchTasks.push('In your first 90 days, focus on learning, building relationships, and quick wins');
  launchTasks.push('Find a mentor or peer group in your new field to accelerate your ramp-up');
  launchTasks.push('Document your career change story -- it becomes a powerful personal brand asset');
  if (form.currentSatisfaction <= 2) {
    launchTasks.push('Prioritize work-life balance in your new role to recover from dissatisfaction or burnout');
  }

  phases.push({
    name: 'Launch',
    duration: 'First 90 days',
    icon: 'M13 10V3L4 14h7v7l9-11h-7z',
    color: 'from-emerald-500 to-teal-500',
    tasks: launchTasks,
  });

  return phases;
}

function buildInsights(form: FormData, motivation: number, readiness: number, financial: number, skills: number): string[] {
  const insights: string[] = [];

  // Highest and lowest dimensions
  const dims = [
    { label: 'Motivation', score: motivation },
    { label: 'Readiness', score: readiness },
    { label: 'Financial Safety', score: financial },
    { label: 'Skills Transferability', score: skills },
  ];
  const sorted = [...dims].sort((a, b) => b.score - a.score);
  const strongest = sorted[0];
  const weakest = sorted[sorted.length - 1];

  insights.push(`Your strongest area is ${strongest.label} (${strongest.score}%). This is a solid foundation to build your career change on.`);

  if (weakest.score < 40) {
    insights.push(`Your biggest gap is ${weakest.label} (${weakest.score}%). Addressing this before making a move will significantly reduce risk.`);
  } else if (weakest.score < 60) {
    insights.push(`${weakest.label} (${weakest.score}%) is your area with the most room for improvement. Small steps here will boost your overall readiness.`);
  }

  // Specific insights
  if (form.currentSatisfaction <= 2 && form.timelineFlexibility === 'urgent') {
    insights.push('You are highly dissatisfied and moving urgently. Be careful not to leap into the first available opportunity -- even a quick transition benefits from strategic targeting.');
  }

  if (form.financialRunwayMonths < 3 && form.secondIncomeSource === 'no') {
    insights.push('With less than 3 months of savings and no secondary income, consider building a financial cushion or transitioning while still employed to reduce financial stress.');
  }

  if (form.networkInTargetField === 'none' && form.relevantExperience === 'none') {
    insights.push('You are starting with no network and no experience in your target field. Prioritize informational interviews and a small volunteer or freelance project to build credibility quickly.');
  }

  if (form.transferableSkillsCount === '8+' && form.educationNeeds === 'none') {
    insights.push('Your skills are highly transferable and you need no additional education. You are well-positioned for a smooth transition -- focus your energy on networking and positioning.');
  }

  if (form.willingnessToStartOver === 'not-willing' && form.educationNeeds !== 'none') {
    insights.push('You expect to maintain your seniority level but still need additional training. This combination is challenging -- consider roles that explicitly value cross-industry experience.');
  }

  if (form.clarityOfTarget === 'very-clear' && form.relevantExperience === 'significant') {
    insights.push('You have both clarity and hands-on experience in your target field. This puts you ahead of most career changers -- lean into your story and unique perspective.');
  }

  return insights.slice(0, 5);
}

function getReadinessLabel(score: number): { label: string; description: string } {
  if (score >= 80) return {
    label: 'Highly Ready',
    description: 'You are in an excellent position to make a career change. Your motivation, preparation, finances, and skills are well aligned. Focus on execution -- start your transition with confidence.',
  };
  if (score >= 65) return {
    label: 'Mostly Ready',
    description: 'You have strong foundations for a career change with a few areas to shore up. Address the gaps identified below and you will be in great shape to make your move.',
  };
  if (score >= 50) return {
    label: 'Getting Ready',
    description: 'You are on the right track but need more preparation before making the leap. Follow the transition plan below to systematically build your readiness over the next few months.',
  };
  if (score >= 35) return {
    label: 'Early Stage',
    description: 'You have the desire to change but significant preparation is still needed. Focus on the Research and Prepare phases below before committing to a timeline.',
  };
  return {
    label: 'Exploration Phase',
    description: 'You are at the beginning of your career change journey. That is perfectly fine -- use the plan below to build clarity, skills, and financial stability before making any big moves.',
  };
}

function calculateResults(form: FormData): Results {
  const motivation = scoreMotivation(form);
  const readiness = scoreReadiness(form);
  const financial = scoreFinancialSafety(form);
  const skills = scoreSkillsTransferability(form);

  // Weighted overall: Motivation 20%, Readiness 25%, Financial 25%, Skills 30%
  const overall = Math.round(
    motivation * 0.2 + readiness * 0.25 + financial * 0.25 + skills * 0.3,
  );

  const dimensions: DimensionScore[] = [
    { label: 'Motivation', score: motivation },
    { label: 'Readiness', score: readiness },
    { label: 'Financial Safety', score: financial },
    { label: 'Skills Transferability', score: skills },
  ];

  const { label: readinessLabel, description: readinessDescription } = getReadinessLabel(overall);

  const transitionPlan = buildTransitionPlan(form, motivation, readiness, financial, skills);
  const topInsights = buildInsights(form, motivation, readiness, financial, skills);

  return { overall, dimensions, readinessLabel, readinessDescription, transitionPlan, topInsights };
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function SectionHeader({ number, total, title, icon }: { number: number; total: number; title: string; icon: string }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
        </svg>
      </div>
      <div>
        <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">Question {number} of {total}</p>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function CareerChangeContent() {
  const [currentStep, setCurrentStep] = useState(-1); // -1 = intro screen
  const [form, setForm] = useState<FormData>(DEFAULT_FORM);
  const [results, setResults] = useState<Results | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const totalQuestions = QUESTIONS.length;
  const progress = currentStep >= 0 ? Math.round(((currentStep + 1) / totalQuestions) * 100) : 0;

  function update<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const handleStart = useCallback(() => {
    setCurrentStep(0);
    setForm(DEFAULT_FORM);
    setResults(null);
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep < totalQuestions - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final question -- compute results
      setIsCalculating(true);
      setTimeout(() => {
        setResults(calculateResults(form));
        setIsCalculating(false);
      }, 1500);
    }
  }, [currentStep, totalQuestions, form]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleRetake = useCallback(() => {
    setCurrentStep(-1);
    setForm(DEFAULT_FORM);
    setResults(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const selectClass =
    'w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white text-gray-900';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <ToolHero
        title="Career Change Assessment"
        description="Assess your readiness for a career change. Get a personalized transition plan based on your motivation, readiness, finances, and skills."
        category="Career Exploration"
      />

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* ============================================================ */}
        {/*  INTRO SCREEN                                                 */}
        {/* ============================================================ */}
        {currentStep === -1 && !results && !isCalculating && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <h2 className="text-2xl font-extrabold text-gray-900 mb-3">
                Are You Ready for a Career Change?
              </h2>
              <p className="text-gray-600 max-w-lg mx-auto mb-8 leading-relaxed">
                Answer 12 quick questions about your motivation, readiness, finances, and skills.
                We will score you across four dimensions and generate a personalized transition plan
                with concrete steps -- no sign-up required.
              </p>

              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
                <div className="text-center">
                  <p className="text-3xl font-extrabold text-blue-600">12</p>
                  <p className="text-xs text-gray-500 font-medium">Questions</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-extrabold text-indigo-600">4</p>
                  <p className="text-xs text-gray-500 font-medium">Dimensions</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-extrabold text-purple-600">4</p>
                  <p className="text-xs text-gray-500 font-medium">Phase Plan</p>
                </div>
              </div>

              <button
                onClick={handleStart}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-4 px-10 rounded-xl shadow-lg hover:from-blue-700 hover:to-indigo-700 transition-all text-lg cursor-pointer"
              >
                Start Assessment
              </button>
              <p className="text-xs text-gray-400 mt-4">
                100% free. No sign-up required. Runs entirely in your browser.
              </p>
            </div>

            {/* What we measure */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Four Dimensions We Measure
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { name: 'Motivation', desc: 'How strong is your drive to make a change?', color: 'from-blue-500 to-cyan-500', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
                  { name: 'Readiness', desc: 'Are your timeline, mindset, and education aligned?', color: 'from-purple-500 to-pink-500', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
                  { name: 'Financial Safety', desc: 'Do you have the runway to weather a transition?', color: 'from-amber-500 to-orange-500', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
                  { name: 'Skills Transferability', desc: 'How well do your current skills carry over?', color: 'from-emerald-500 to-teal-500', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
                ].map((dim) => (
                  <div
                    key={dim.name}
                    className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${dim.color} flex items-center justify-center shrink-0`}>
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={dim.icon} />
                      </svg>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">{dim.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{dim.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <ToolCTA
              headline="Already know your next career? Build a resume for it."
              description="Superpower Resume helps you craft a targeted, ATS-optimized resume for any career path in minutes."
              buttonText="Build My Resume"
            />
          </div>
        )}

        {/* ============================================================ */}
        {/*  QUESTIONNAIRE                                                */}
        {/* ============================================================ */}
        {currentStep >= 0 && !results && !isCalculating && (
          <div>
            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-600">
                  Question {currentStep + 1} of {totalQuestions}
                </span>
                <span className="text-sm font-bold text-blue-600">{progress}%</span>
              </div>
              <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Question card */}
            {(() => {
              const q = QUESTIONS[currentStep];
              return (
                <div
                  key={q.id}
                  className={`bg-white rounded-2xl shadow-lg border-l-4 ${q.accent.split(' ')[0]} p-8`}
                >
                  <SectionHeader
                    number={currentStep + 1}
                    total={totalQuestions}
                    title={q.subtitle}
                    icon={q.icon}
                  />

                  <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 mb-8 leading-tight">
                    {q.question}
                  </h2>

                  {/* Slider input */}
                  {q.type === 'slider' && q.id === 'currentSatisfaction' && (
                    <div>
                      <div className="flex items-center gap-4 mb-3">
                        <input
                          type="range"
                          min={q.sliderMin}
                          max={q.sliderMax}
                          value={form[q.id] as number}
                          onChange={(e) => update(q.id, Number(e.target.value) as never)}
                          className="flex-1 accent-blue-600 h-2 rounded-lg cursor-pointer"
                        />
                        <span className="w-40 text-center text-sm font-bold text-blue-700 bg-blue-50 rounded-lg py-2 px-3">
                          {SATISFACTION_LABELS[form[q.id] as number]}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-1 px-1">
                        <span>Very Dissatisfied</span>
                        <span>Neutral</span>
                        <span>Very Satisfied</span>
                      </div>
                    </div>
                  )}

                  {q.type === 'slider' && q.id === 'financialRunwayMonths' && (
                    <div>
                      <div className="flex items-center gap-4 mb-3">
                        <input
                          type="range"
                          min={q.sliderMin}
                          max={q.sliderMax}
                          value={form[q.id] as number}
                          onChange={(e) => update(q.id, Number(e.target.value) as never)}
                          className="flex-1 accent-blue-600 h-2 rounded-lg cursor-pointer"
                        />
                        <span className="w-24 text-center text-lg font-bold text-blue-700 bg-blue-50 rounded-lg py-1">
                          {form[q.id] as number}{(form[q.id] as number) >= 12 ? '+' : ''} {q.sliderUnit}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>0</span>
                        <span>6</span>
                        <span>12+</span>
                      </div>
                    </div>
                  )}

                  {/* Select input */}
                  {q.type === 'select' && q.options && (
                    <div className="space-y-3">
                      {q.options.map((option) => {
                        const isSelected = form[q.id] === option.value;
                        return (
                          <button
                            key={option.value}
                            onClick={() => update(q.id, option.value as never)}
                            className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                                  isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                                }`}
                              >
                                {isSelected && (
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <span className="text-sm sm:text-base font-medium text-gray-800 leading-relaxed">
                                {option.label}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Navigation buttons */}
                  <div className="flex items-center justify-between mt-8">
                    <button
                      onClick={handleBack}
                      disabled={currentStep === 0}
                      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-colors cursor-pointer ${
                        currentStep === 0
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back
                    </button>
                    <button
                      onClick={handleNext}
                      className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl shadow-lg hover:from-blue-700 hover:to-indigo-700 transition-all cursor-pointer"
                    >
                      {currentStep < totalQuestions - 1 ? 'Next' : 'See My Results'}
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ============================================================ */}
        {/*  CALCULATING SCREEN                                           */}
        {/* ============================================================ */}
        {isCalculating && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center animate-pulse">
              <svg className="w-8 h-8 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">
              Analyzing your readiness...
            </h2>
            <p className="text-gray-500">
              Scoring your responses across 4 dimensions and building your personalized transition plan.
            </p>
          </div>
        )}

        {/* ============================================================ */}
        {/*  RESULTS                                                      */}
        {/* ============================================================ */}
        {results && (
          <div className="space-y-8">
            {/* Overall Score */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Your Career Change Readiness Score
              </h2>
              <div className="flex justify-center mb-6">
                <ScoreGauge score={results.overall} label="Overall Readiness" size={240} />
              </div>
              <div className="text-center">
                <span className="inline-block bg-blue-100 text-blue-800 font-bold text-sm px-4 py-1.5 rounded-full mb-3">
                  {results.readinessLabel}
                </span>
                <p className="text-gray-600 max-w-lg mx-auto leading-relaxed">
                  {results.readinessDescription}
                </p>
              </div>
            </div>

            {/* Dimension Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Dimension Breakdown</h3>
              <div className="space-y-5">
                {results.dimensions.map((dim) => (
                  <ProgressBar key={dim.label} value={dim.score} label={dim.label} />
                ))}
              </div>
            </div>

            {/* Key Insights */}
            {results.topInsights.length > 0 && (
              <div className="bg-blue-50 rounded-2xl border border-blue-200 p-6">
                <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Key Insights
                </h3>
                <ul className="space-y-3">
                  {results.topInsights.map((insight, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center text-xs font-bold text-blue-700 shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <span className="text-sm text-blue-800 leading-relaxed">{insight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Transition Plan */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Your Personalized Transition Plan
              </h3>
              <p className="text-sm text-gray-500 mb-8">
                A phased approach tailored to your assessment results. Start at Phase 1 and progress sequentially.
              </p>

              <div className="space-y-6">
                {results.transitionPlan.map((phase, phaseIdx) => (
                  <div key={phase.name} className="relative">
                    {/* Connector line */}
                    {phaseIdx < results.transitionPlan.length - 1 && (
                      <div className="absolute left-5 top-14 bottom-0 w-0.5 bg-gray-200" />
                    )}

                    <div className="flex items-start gap-4">
                      {/* Phase icon */}
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${phase.color} flex items-center justify-center shrink-0 relative z-10`}>
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={phase.icon} />
                        </svg>
                      </div>

                      <div className="flex-1 min-w-0 pb-6">
                        <div className="flex items-center gap-3 flex-wrap mb-3">
                          <h4 className="text-lg font-extrabold text-gray-900">
                            Phase {phaseIdx + 1}: {phase.name}
                          </h4>
                          <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                            {phase.duration}
                          </span>
                        </div>

                        <ul className="space-y-2.5">
                          {phase.tasks.map((task, taskIdx) => (
                            <li key={taskIdx} className="flex items-start gap-2.5">
                              <svg className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                              <span className="text-sm text-gray-700 leading-relaxed">{task}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Financial Snapshot */}
            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-amber-900 mb-1">Financial Snapshot</h3>
                  <p className="text-amber-800 leading-relaxed">
                    {form.financialRunwayMonths >= 9
                      ? `With ${form.financialRunwayMonths}+ months of savings, you have a strong financial cushion for a career transition. This gives you the freedom to be selective and strategic.`
                      : form.financialRunwayMonths >= 6
                        ? `With ~${form.financialRunwayMonths} months of savings, you have a reasonable runway. Consider building it to 9-12 months for a more comfortable transition, especially if your target field requires additional training.`
                        : form.financialRunwayMonths >= 3
                          ? `With ~${form.financialRunwayMonths} months of savings, your financial runway is tight for a career change. Strongly consider transitioning while still employed, or building your savings before making the leap.`
                          : `With less than 3 months of savings, a career change carries significant financial risk. Focus on building your emergency fund and exploring your new direction before leaving your current role.`}
                    {form.secondIncomeSource === 'yes-strong' && ' Having a strong secondary income source significantly reduces your risk.'}
                    {form.secondIncomeSource === 'yes-some' && ' Your secondary income provides a helpful buffer, though it may not fully replace your primary salary.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Tips Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Do */}
              <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-6">
                <h3 className="text-lg font-bold text-emerald-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Do This
                </h3>
                <ul className="space-y-3">
                  {[
                    'Test your target career before fully committing (freelance, volunteer, shadow)',
                    'Build your network in the new field before you need it',
                    'Frame your career change as an asset, not an apology',
                    'Set clear milestones and deadlines for each transition phase',
                  ].map((tip, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-emerald-500 shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-emerald-800 leading-relaxed">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Avoid */}
              <div className="bg-red-50 rounded-2xl border border-red-200 p-6">
                <h3 className="text-lg font-bold text-red-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  Avoid This
                </h3>
                <ul className="space-y-3">
                  {[
                    'Quitting your current job before having a plan and financial cushion',
                    'Romanticizing the new career without understanding day-to-day realities',
                    'Trying to change everything at once -- career, location, lifestyle',
                    'Undervaluing your existing experience when talking to employers',
                  ].map((tip, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-red-500 shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-sm text-red-800 leading-relaxed">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Actions: Retake */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleRetake}
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retake Assessment
              </button>
            </div>

            {/* Educational Content */}
            <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">The Science Behind Successful Career Changes</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Career transitions are more common than many people realize. According to the Bureau of Labor Statistics,
                  the average American holds over 12 jobs between the ages of 18 and 54, and a growing share of those moves
                  involve switching industries or occupations entirely. Research from the U.S. Department of Labor shows
                  that transferable skills -- communication, project management, analytical thinking, and leadership --
                  are valued across nearly every occupation, which means career changers often bring more to the table
                  than they expect.
                </p>
                <p>
                  The four dimensions measured in this assessment -- motivation, readiness, financial safety, and skills
                  transferability -- are grounded in career development research. Studies published in the Harvard Business
                  Review suggest that successful career changers share common traits: they test the new path before
                  committing (through freelancing, volunteering, or side projects), they build networks in their target
                  field early, and they frame their transition as a strength rather than a gap. Financial preparedness
                  is equally critical; career coaches recommend maintaining at least six months of living expenses before
                  making a full transition.
                </p>
                <p>
                  Tools like O*NET and MyNextMove, both maintained by the U.S. Department of Labor, allow you to explore
                  occupations by skills, interests, and education requirements. They can help you identify target roles
                  where your existing experience creates a competitive advantage, even if the job title looks very
                  different from what you have done before.
                </p>
                <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <strong>Sources &amp; Further Reading:</strong>{' '}
                  <a href="https://www.onetonline.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">O*NET OnLine &ndash; Explore Occupations by Skills &amp; Interests</a>
                  {' · '}
                  <a href="https://www.mynextmove.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">MyNextMove &ndash; U.S. Department of Labor Career Exploration</a>
                  {' · '}
                  <a href="https://hbr.org/topic/career-planning" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review &ndash; Career Planning &amp; Transitions</a>
                </p>
              </div>
            </section>

            {/* CTA */}
            <ToolCTA
              headline="Ready to make your move? Build a career-change resume."
              description="Superpower Resume helps you reframe your experience, highlight transferable skills, and craft a resume that gets you into your new career."
              buttonText="Build My Resume"
            />
          </div>
        )}
      </div>
    </div>
  );
}
